import { LocationModule } from '@/src/hooks/location-module/location-module.types'
import { checkIsModuleEnabled } from '@/src/hooks/location-module/location-module.utils'
import { LayoutMain } from '@/src/layouts/LayoutMain/LayoutMain'
import { AuthRoute } from '@/src/routes/routes.components'
import { EFFICIENCY_AND_MEASURES_BASE_ROUTE, TABS } from '@/src/utils/constants'
import { Suspense, lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import { EnPIProvider } from './enpi/EnPIContext' // Import the EnPIProvider

const ISOSuitePageLazy = lazy(() => import('./ISOSuiteScreen.page'))
const ISOOverviewPageLazy = lazy(() => import('./overview/IsoOverview.page'))
const ISOSuiteDocumentationPageLazy = lazy(() => import('./documentation/ISOSuiteDocumentation.page'))
const ISODocumentDetailPageLazy = lazy(() => import('./documentation/document/IsoDocumentDetail.screen'))
const ISODocumentCreatePageLazy = lazy(() => import('./documentation/document/create/IsoDocumentCreation.screen'))
const OrgContextLazy = lazy(() => import('./organization-context/OrganizationContext'))
const EnergyTeamLazy = lazy(() => import('./energy-team/EnergyTeam'))
const ISOSuiteEnergyGoalsPageLazy = lazy(() => import('./energy-goals/ISOSuiteEnergyGoals.page'))
const ISOSuiteCreateEditEnPIPageLazy = lazy(() => import('./enpi/CreateEditEnPI.page'))
const ISOMeasureV2PageLazy = lazy(() => import('./measures/IsoMeasuresV2.page'))
const ISOMeasureDetailPageLazy = lazy(() => import('./measures/mesaure-detail/IsoMeasuresDetail.page'))
const IsoChecklistScreen = lazy(() => import('./checklist/screens/IsoChecklist.screen'))
const IsoChecklistTaskScreen = lazy(() => import('./checklist/screens/IsoChecklistTask.screen'))
const ISOSuiteValeriPageLazy = lazy(() => import('./valeri/iso-valeri.screen'))
const IsoValeriDetaiPageLazy = lazy(() => import('./valeri/IsoValeriDetail.page'))
const ISOValeriEditPageLazy = lazy(() => import('./valeri/IsoValeriEdit.page'))

const ISOSuitePage = (
  <Suspense fallback={<div></div>}>
    <EnPIProvider>
      <ISOSuitePageLazy />
    </EnPIProvider>
  </Suspense>
)

const ISOOverviewPage = (
  <Suspense fallback={<div></div>}>
    <ISOOverviewPageLazy />
  </Suspense>
)

const ISOSuiteDocumentationPage = (
  <Suspense fallback={<div></div>}>
    <ISOSuiteDocumentationPageLazy />
  </Suspense>
)

const IsoDocumentDetailPage = (
  <Suspense fallback={<div></div>}>
    <ISODocumentDetailPageLazy />
  </Suspense>
)

const IsoDocumentCreatePage = (
  <Suspense fallback={<div></div>}>
    <ISODocumentCreatePageLazy />
  </Suspense>
)

const OrgContext = (
  <Suspense fallback={<div></div>}>
    <OrgContextLazy />
  </Suspense>
)

const EnergyTeam = (
  <Suspense fallback={<div></div>}>
    <EnergyTeamLazy />
  </Suspense>
)

const EnergyGoalsPage = (
  <Suspense fallback={<div></div>}>
    <ISOSuiteEnergyGoalsPageLazy />
  </Suspense>
)

const ISOSuiteCreateEditEnPIPage = (
  <Suspense fallback={<div></div>}>
    <ISOSuiteCreateEditEnPIPageLazy />
  </Suspense>
)

const ISOMeasuresPage = () => {
  return (
    <Suspense fallback={<div></div>}>
      <ISOMeasureV2PageLazy />
    </Suspense>
  )
}

const ISOMeasuresDetailPage = (
  <Suspense fallback={<div></div>}>
    <ISOMeasureDetailPageLazy />
  </Suspense>
)

const ISOSuiteValeriPage = (
  <Suspense fallback={<div></div>}>
    <ISOSuiteValeriPageLazy />
  </Suspense>
)

const IsoChecklistPage = (
  <Suspense fallback={<div></div>}>
    <IsoChecklistScreen />
  </Suspense>
)

const IsoChecklistTaskPage = (
  <Suspense fallback={<div></div>}>
    <IsoChecklistTaskScreen />
  </Suspense>
)

const ValeriDetaiPage = (
  <Suspense fallback={<div></div>}>
    <IsoValeriDetaiPageLazy />
  </Suspense>
)

const ISOValeriEditPage = (
  <Suspense fallback={<div></div>}>
    <ISOValeriEditPageLazy />
  </Suspense>
)

export const getIsoSuiteRoutesV2 = (enabledModules?: LocationModule[]): RouteObject[] => {
  const defaultRedirection = checkIsModuleEnabled(LocationModule.ISO50001, enabledModules)
    ? TABS.efficiencyAndMeasures.overview
    : TABS.efficiencyAndMeasures.measures

  return [
    {
      path: EFFICIENCY_AND_MEASURES_BASE_ROUTE,
      element: (
        <AuthRoute>
          <LayoutMain />
        </AuthRoute>
      ),
      children: [
        { path: '', element: <Navigate replace to={defaultRedirection} /> },
        { path: 'overview', element: ISOSuitePage, children: [{ path: '', element: ISOOverviewPage }] },
        {
          path: 'checklist',
          element: ISOSuitePage,
          children: [
            { path: '', element: IsoChecklistPage, children: [{}] },
            { path: 'task', element: IsoChecklistTaskPage },
          ],
        },
        { path: 'energy-team', element: ISOSuitePage, children: [{ path: '', element: EnergyTeam }] },
        { path: 'organization-context', element: ISOSuitePage, children: [{ path: '', element: OrgContext }] },
        { path: 'energy-goals', element: ISOSuitePage, children: [{ path: '', element: EnergyGoalsPage }] },
        {
          path: 'documentation',
          element: ISOSuitePage,
          children: [
            { path: '', element: ISOSuiteDocumentationPage },
            { path: ':documentId', element: IsoDocumentDetailPage },
            {
              path: 'new',
              element: ISOSuitePage,
              children: [
                { path: '', element: <Navigate replace to='..' /> },
                { path: 'ecoplanet', element: IsoDocumentCreatePage },
                { path: 'document', element: IsoDocumentCreatePage },
                { path: 'link', element: IsoDocumentCreatePage },
              ],
            },
          ],
        },
        {
          path: 'measures',
          element: ISOSuitePage,
          children: [
            { path: '', element: <ISOMeasuresPage /> },
            { path: ':measureId', element: ISOMeasuresDetailPage },
          ],
        },
        {
          path: 'valeri',
          element: ISOSuitePage,
          children: [
            { path: '', element: ISOSuiteValeriPage },
            { path: ':valeriId', element: ValeriDetaiPage },
            { path: ':valeriId/edit', element: ISOValeriEditPage },
          ],
        },
        { path: 'documentation', element: ISOSuitePage, children: [{ path: '', element: <div>IMPLEMENT ME!</div> }] },
      ],
    },
  ]
}
